/**
 * Sales Assist Panel - Phase 1
 * Shows shopper's online activity for sales team context
 * ADDITIVE component - does not modify unlock feature (READ-ONLY)
 */
import React, { useState, useEffect } from 'react';
import { 
  Activity, Clock, Car, Eye, MousePointerClick, 
  TrendingUp, Calendar, MapPin, ChevronDown, ChevronUp,
  Lock, Unlock, RefreshCw
} from 'lucide-react';
import InfoTooltip from "../InfoTooltip";

// Resolve backend URL via window.location.origin so this works for
// preview, custom prod domain, and default emergent.host alike.
const getBackendUrl = () => {
  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};
const API_URL = getBackendUrl();

/**
 * Format vehicle identifier for display
 * Converts internal prefixes to user-friendly labels
 */
const formatVehicleId = (rawId) => {
  if (!rawId) return null;
  
  // Remove internal prefixes and format for display
  if (rawId.startsWith('VEHICLE_ID:')) {
    return rawId.replace('VEHICLE_ID:', 'Stock #');
  }
  if (rawId.startsWith('ID:')) {
    return rawId.replace('ID:', '#');
  }
  if (rawId.startsWith('PATH:')) {
    return rawId.replace('PATH:', 'Ref: ');
  }
  if (rawId.startsWith('UNKNOWN:')) {
    return null; // Don't display unknown identifiers
  }
  
  // Check if it's a valid 17-char VIN - show last 6 chars
  const isValidVin = /^[A-HJ-NPR-Z0-9]{17}$/i.test(rawId);
  if (isValidVin) {
    return `VIN: ...${rawId.slice(-6)}`;
  }
  
  // Fallback: display as-is
  return rawId;
};

export const SalesAssistPanel = ({ token, leadId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showTimeline, setShowTimeline] = useState(false);
  
  // Check for debug mode via URL query param
  const isDebugMode = typeof window !== 'undefined' && 
    new URLSearchParams(window.location.search).get('debug') === '1';

  useEffect(() => {
    const fetchData = async () => {
      if (!token || !leadId) return;
      
      try {
        setLoading(true);
        
        // Build headers - include debug header if debug mode
        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        };
        if (isDebugMode) {
          headers['X-VD-Debug'] = '1';
        }
        
        const response = await fetch(`${API_URL}/api/dashboard/leads/${leadId}/sales-assist`, {
          headers,
        });
        
        if (!response.ok) {
          const errData = await response.json();
          throw new Error(errData.detail || 'Failed to fetch sales assist data');
        }
        
        const result = await response.json();
        
        setData(result);
        setError(null);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error fetching sales assist data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token, leadId, isDebugMode]);

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  // iter82 — Bug 10: friendly Activity Timeline labels. Add an
  // EVENT_LABELS table for the human-facing copy. Anything not in
  // the table falls back to underscore→space + Title Case so even
  // newly added event types remain readable instead of leaking
  // their snake_case identifier into the UI.
  const EVENT_LABELS = {
    vehicle_view: 'Vehicle Viewed',
    vehicle_repeat_view: 'Repeat View',
    time_engaged: 'Engaged',
    unlock_attempt: 'Unlock Attempt',
    unlock_completed: 'Unlock Completed',
    unlock_initiated: 'Unlock Initiated',
    price_band_interest: 'Price Interest',
    price_revealed: 'Price Revealed',
    sms_verified: 'SMS Verified',
    sms_sent: 'SMS Sent',
    hot_return: 'Hot Return',
    page_view: 'Page View',
    form_started: 'Form Started',
    form_submitted: 'Form Submitted',
    cta_click: 'CTA Click',
    lead_created: 'Lead Created',
    deal_won: 'Deal Won',
    deal_lost: 'Deal Lost',
  };

  const formatEventType = (type) => {
    if (!type) return '—';
    if (EVENT_LABELS[type]) return EVENT_LABELS[type];
    // Fallback: snake_case → "Title Case".
    return String(type)
      .replace(/[_-]+/g, ' ')
      .trim()
      .replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
  };

  const getEventIcon = (type) => {
    switch (type) {
      case 'vehicle_view':
      case 'vehicle_repeat_view':
        return <Eye className="w-4 h-4 text-blue-500" />;
      case 'unlock_attempt':
        return <Unlock className="w-4 h-4 text-green-500" />;
      case 'time_engaged':
        return <Clock className="w-4 h-4 text-purple-500" />;
      default:
        return <Activity className="w-4 h-4 text-gray-500" />;
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-lg border p-6" data-testid="sales-assist-panel">
        <div className="flex items-center justify-center h-48">
          <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-lg border p-6" data-testid="sales-assist-panel">
        <div className="text-center text-red-600">
          <p className="font-medium">Error loading data</p>
          <p className="text-sm mt-1">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow-lg border" data-testid="sales-assist-panel">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Activity className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 inline-flex items-center" data-testid="sales-assist-title">
                Sales Assist
                <InfoTooltip
                  testId="sales-assist-info-tooltip"
                  label="What is Sales Assist?"
                  side="bottom"
                  maxWidth={320}
                >
                  <strong className="block mb-1">Sales Assist</strong>
                  A panel that shows what this shopper actually did on your
                  website <em>before</em> you contacted them — so your rep
                  walks into the call already knowing what the buyer cares about.
                  <span className="block mt-2 font-semibold">Metrics:</span>
                  <span className="block">• <strong>Events</strong> — total interactions on site</span>
                  <span className="block">• <strong>Vehicles Viewed</strong> — how many different vehicles they clicked into</span>
                  <span className="block">• <strong>Minutes Engaged</strong> — total time on your site during tracked sessions</span>
                  <span className="block">• <strong>Price Status</strong> — Unlocked or Locked</span>
                </InfoTooltip>
              </h3>
              <p className="text-sm text-gray-600">
                {data.lead_name || 'Shopper'} • ...{data.lead_phone || '****'}
              </p>
            </div>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          )}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-6 border-b border-gray-200">
        <div className="text-center">
          <div className="text-2xl font-bold text-gray-900">
            {data.summary?.total_events || 0}
          </div>
          <div className="text-xs text-gray-500 uppercase tracking-wide">Events</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-gray-900">
            {data.summary?.vehicles_viewed_count || 0}
          </div>
          <div className="text-xs text-gray-500 uppercase tracking-wide">Vehicles Viewed</div>
        </div>
        <div className="text-center group relative">
          <div className="text-2xl font-bold text-gray-900">
            {(data.summary?.total_time_spent_minutes || 0) === 0 
              ? "< 1" 
              : data.summary?.total_time_spent_minutes}
          </div>
          <div className="text-xs text-gray-500 uppercase tracking-wide cursor-help">
            Minutes Engaged
            <span className="hidden group-hover:block absolute left-1/2 -translate-x-1/2 top-full mt-2 px-3 py-2 bg-gray-900 text-white text-xs rounded shadow-lg whitespace-nowrap z-10">
              Time on site during tracked sessions
            </span>
          </div>
        </div>
        <div className="text-center">
          {data.unlock_info ? (
            <>
              <div className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                <Unlock className="w-4 h-4" />
                Unlocked
              </div>
              <div className="text-xs text-gray-500 uppercase tracking-wide mt-1">Price Status</div>
            </>
          ) : (
            <>
              <div className="text-2xl font-bold text-gray-900">
                {data.summary?.event_types?.unlock_attempt || 0}
              </div>
              <div className="text-xs text-gray-500 uppercase tracking-wide inline-flex items-center justify-center">
                Unlock Attempts
                {/* iter98 — Tooltips: explain "Unlock Attempts" value */}
                <InfoTooltip testId="tooltip-unlock-attempts" maxWidth={260}>
                  The number of times this visitor attempted to unlock the price before successfully completing the form. A value of 0 means they unlocked on the first attempt.
                </InfoTooltip>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Unlock Status */}
      {data.unlock_info && (
        <div className="px-6 py-4 bg-green-50 border-b border-green-100">
          <div className="flex items-center gap-2 text-green-700">
            <Unlock className="w-5 h-5" />
            <span className="font-medium">Price Unlocked</span>
          </div>
          <div className="mt-2 text-sm text-green-600 space-y-1">
            {data.unlock_info.unlocked_at && (
              <p>Unlocked: {formatTimeAgo(data.unlock_info.unlocked_at)}</p>
            )}
            {data.unlock_info.last_seen_at && (
              <p>Last seen: {formatTimeAgo(data.unlock_info.last_seen_at)}</p>
            )}
            {data.unlock_info.vin && formatVehicleId(data.unlock_info.vin) && (
              <p className="flex items-center gap-1">
                <Car className="w-4 h-4" />
                {formatVehicleId(data.unlock_info.vin)}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Vehicles Viewed */}
      {data.vehicles_viewed && data.vehicles_viewed.length > 0 && (
        <div className="px-6 py-4 border-b border-gray-200">
          <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
            <Car className="w-4 h-4 text-gray-500" />
            Vehicles Viewed
          </h4>
          <div className="space-y-2">
            {data.vehicles_viewed.slice(0, 5).map((vehicle, idx) => {
              // Use title from backend (handles YMM + fallback to formatted vehicle_id)
              const displayTitle = vehicle.title || 
                `${vehicle.year || ''} ${vehicle.make || ''} ${vehicle.model || ''}`.trim() || 
                formatVehicleId(vehicle.vehicle_id) ||
                'Unknown Vehicle';
              
              return (
                <div 
                  key={idx}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  data-testid={`vehicle-viewed-${idx}`}
                >
                  <div>
                    <p className="font-medium text-gray-900">
                      {displayTitle}
                    </p>
                    {vehicle.trim && (
                      <p className="text-sm text-gray-500">{vehicle.trim}</p>
                    )}
                    {vehicle.price && (
                      <p className="text-sm text-green-600 font-medium">
                        ${vehicle.price.toLocaleString()}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-1 text-sm text-gray-600">
                      <Eye className="w-4 h-4" />
                      {vehicle.view_count}x
                    </div>
                    {vehicle.total_time_seconds > 0 && (
                      <div className="text-xs text-gray-500">
                        {Math.round(vehicle.total_time_seconds / 60)}m engaged
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Activity Timeline (Collapsible) */}
      <div className="px-6 py-4">
        <button
          onClick={() => setShowTimeline(!showTimeline)}
          className="w-full flex items-center justify-between text-left"
          data-testid="toggle-timeline-btn"
        >
          <h4 className="font-medium text-gray-900 flex items-center gap-2">
            <Clock className="w-4 h-4 text-gray-500" />
            Activity Timeline
          </h4>
          {showTimeline ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>
        
        {showTimeline && data.timeline && (
          <div className="mt-4 space-y-3 max-h-64 overflow-y-auto">
            {data.timeline.map((event, idx) => (
              <div 
                key={idx}
                className="flex items-start gap-3 text-sm"
              >
                <div className="mt-0.5">
                  {getEventIcon(event.event_type)}
                </div>
                <div className="flex-1">
                  <p className="text-gray-900">
                    {formatEventType(event.event_type)}
                    {event.vehicle && (
                      <span className="text-gray-600"> - {event.vehicle}</span>
                    )}
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatTimeAgo(event.timestamp)}
                    {event.time_spent_seconds > 0 && (
                      <span> • {Math.round(event.time_spent_seconds)}s</span>
                    )}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 text-xs text-gray-500">
        Lead created: {formatTimeAgo(data.lead_created_at)}
      </div>
    </div>
  );
};

export default SalesAssistPanel;
